import { Component, computed, effect, inject, signal } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AuthService } from './services/auth'
import { Router } from '@angular/router'
import { addIcons } from 'ionicons'
import { closeOutline, notificationsOutline } from 'ionicons/icons'
import { IonicSharedModule } from './shared/ionic-shared.module'
import { FcmService } from './services/FcmService.service'
import { PushPermissionModalComponent } from './components/push-permission-modal/push-permission-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatrixService } from './services/matrix.service'
import { UserNewsService } from './services/userNewsService'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { filter } from 'rxjs'

@Component({
    selector: 'app-root',
    styleUrls: ['./app.component.scss'],
    templateUrl: './app.component.html',
    standalone: true,
    imports: [IonApp, IonicSharedModule, PushPermissionModalComponent],
})

export class AppComponent {
    private authService = inject(AuthService);
    private router = inject(Router);
    private fcmService = inject(FcmService);
    private translate = inject(TranslateService);
    private matrixService = inject(MatrixService);
    private userNewsService = inject(UserNewsService);
    private swUpdate = inject(SwUpdate);

    private readonly isPwa = ('standalone' in window.navigator
        && (window.navigator as any).standalone === true)
        || window.matchMedia('(display-mode: standalone)').matches;

    public get logoutSheetButtons() {
        return [{
            text: this.translate.instant('menu.logout'), role: 'destructive', data: {
                action: 'logout',
            },
        }, {
            text: this.translate.instant('menu.logout_cancel'), role: 'cancel', data: {
                action: 'cancel',
            },
        }];
    }

    isAuthenticated = computed(() => !!this.authService.userData());
    user = this.authService.userData;
    showPermissionModal = this.fcmService.showPermissionModalTrigger;

    constructor() {
        addIcons({closeOutline, notificationsOutline});
        this.init();
        this.initAppUpdate();
        this.translate.addLangs(['en', 'de']);
        this.translate.use('de');

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' &&
                this.matrixService.roomsLoaded()) {
                void this.updatePwaBadge(
                    this.matrixService.totalUnreadCount()
                );
            }
        });

        effect(() => {
            const user = this.authService.userData();
            // if (user?.type === 'ADMIN') {
                this.getNetiquette();
            // }
        });

        effect(() => {
            const roomsLoaded = this.matrixService.roomsLoaded();
            const unreadCount = this.matrixService.totalUnreadCount();
            if (!this.isPwa || !roomsLoaded) {
                return;
            }
            void this.updatePwaBadge(unreadCount);
        });
    }

    private initAppUpdate(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.swUpdate.versionUpdates
            .pipe(
                filter(
                    (event): event is VersionReadyEvent =>
                        event.type === 'VERSION_READY'
                )
            )
            .subscribe(() => {
                window.location.reload();
            });

        void this.swUpdate.checkForUpdate();

        setInterval(() => {
            void this.swUpdate.checkForUpdate();
        }, 60 * 1000);
    }

    private async updatePwaBadge(unreadCount: number): Promise<void> {
        const badgeNavigator = navigator as Navigator & {
            setAppBadge?: (count?: number) => Promise<void>;
            clearAppBadge?: () => Promise<void>;
        };

        if (!badgeNavigator.setAppBadge || !badgeNavigator.clearAppBadge) return;

        try {
            if (unreadCount > 0) {
                await badgeNavigator.setAppBadge(unreadCount);
            } else {
                await badgeNavigator.clearAppBadge();
            }
        } catch (error) {
            console.warn('Failed to update PWA badge:', error);
        }
    }

    getNetiquette() {
        this.userNewsService.getNetiquette().subscribe({
            next: (data: any) => {
                if (data?.data) {
                    this.matrixService.netiquette.set(data.data);
                } else {
                    this.matrixService.netiquette.set('');
                }
            }, error: (err) => {
                this.matrixService.netiquette.set('');
                console.error('Error fetching netiquette:', err);
            }
        });
    }

    async init() {
        const token = await this.authService.getToken();
        const userData = await this.authService.getUserData();
        const currentPath = window.location.pathname;
        const shouldRedirectToHome = currentPath === '/' || currentPath === '/login';
        const isExternalLink = currentPath.startsWith('/external-link');

        if (token && userData) {
            const parsedUser = typeof userData === 'string' ? JSON.parse(userData) : userData;
            await this.authService.setUserData(parsedUser);
            this.getInfo();

            await this.fcmService.triggerPermissionCheck();

            if (shouldRedirectToHome) {
                await this.router.navigate(['/home']);
            }
        } else if (!isExternalLink){
            await this.router.navigate(['/login']);
        }
    }

    async onPermissionRequested() {
        await this.fcmService.requestPermission();
    }

    closePermissionModal() {
        this.fcmService.showPermissionModalTrigger.set(false);
    }

    getInfo() {
        this.authService.getMe().subscribe({
            next: async (data: any) => {
                if (data && !data?.error) {
                    await this.authService.setUserData(data);
                } else if (data?.error && data?.error === 'token_expired_or_invalid'){
                    this.logout();
                }
            },
            error: (error) => {
                if (error?.status === 401 || error?.status === 403) {
                    this.logout();
                }
                console.error('Error fetching user info:', error);
            }
        })
    }

    async logout() {
        try {
            this.authService.logoutApi().subscribe({
            next: async (data: any) => {
            },
            error: async (error) => {
                console.error('Error logging out:', error);
            }
        })
        } catch (error) {
            console.error('Error logging out:', error);
        }
        await this.authService.logout();
        if ('clearAppBadge' in navigator) {
          await navigator.clearAppBadge();
        }
        await this.router.navigate(['/login']);
    }

    onLogoutSheetDismissed(event: any) {
        if (event.detail.data.action === 'logout') {
            this.logout();
        }
    }

    async openProfile() {
        await this.router.navigate(['/profile']);
    }

    async openAbsence() {
        await this.router.navigate(['/absence']);
    }

    async openHome() {
        await this.router.navigate(['/home']);
    }
}
